import axios from 'axios'
import {
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession
} from 'amazon-cognito-identity-js'
import { cognitoConfig } from '../config'
import jwtDecode from 'jwt-decode'

const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.userPoolId || '',
  ClientId: cognitoConfig.clientId || '',
  endpoint: cognitoConfig.endpoint
})

/**
 * Get the current user.
 */
export const getCurrentUser = (): CognitoUser | null => {
  return userPool.getCurrentUser()
}

/**
 * Get the session.
 */
export const getSession = (): CognitoUserSession | null => {
  const user = userPool.getCurrentUser()

  return user?.getSignInUserSession() ?? null
}

/**
 * Get the ID token.
 */
export const getIdToken = (): string | null => {
  const user = userPool.getCurrentUser()

  return user?.getSignInUserSession()?.getIdToken()?.getJwtToken() ?? null
}

/**
 * Get the access token.
 */
// export const getAccessToken = (): string | null => {
//   const user = userPool.getCurrentUser()
//
//   return user?.getSignInUserSession()?.getAccessToken()?.getJwtToken() ?? null
// }

/**
 * Get the refresh token.
 */
// export const getRefreshToken = (): string | null => {
//   const user = userPool.getCurrentUser()
//
//   return user?.getSignInUserSession()?.getRefreshToken()?.getToken() ?? null
// }

/**
 * Functions to check if accessToken is still valid
 * @returns Is accessToken valid
 */
export async function isValidSession() {
  const user = userPool.getCurrentUser()

  user?.getSession(async (_err: Error | null, session: CognitoUserSession | null) => {
    const accessToken = session?.getAccessToken()?.getJwtToken()

    if (!accessToken) {
      return false
    }

    const decoded = jwtDecode<{ exp: number }>(accessToken)
    const now = Date.now() / 1000

    return decoded.exp > now
  })
}

/**
 * Function to find accessToken from the localStorage
 * @returns accessToken
 */
export function getAccessToken() {
  let accessToken = null
  const regex = new RegExp(/^CognitoIdentityService(.*)accessToken/)

  for (const key in localStorage) {
    if (regex.test(key)) {
      accessToken = localStorage.getItem(key)
      break
    }
  }

  return accessToken
}

/**
 * Funtion to find refreshToken from the localStorage
 * @returns refreshToken
 */
export function getRefreshToken() {
  let refreshToken = null
  const regex = new RegExp(/^CognitoIdentityService(.*)refreshToken/)

  for (const key in localStorage) {
    if (regex.test(key)) {
      refreshToken = localStorage.getItem(key)
    }
  }
  return refreshToken
}

/**
 * Function to refresh session after accessToken expires
 * @returns Refreshed session
 */
export async function handleRefreshSession() {
  return await new Promise((resolve, reject) => {
    const user = userPool.getCurrentUser()

    user?.getSession(async (_err: Error | null, session: CognitoUserSession | null) => {
      const refreshToken = session?.getRefreshToken().getToken()

      if (refreshToken) {
        const token = new CognitoRefreshToken({
          RefreshToken: refreshToken
        })

        user.refreshSession(token, (err, session) => {
          const accessToken = session?.getAccessToken().getJwtToken()
          if (err) {
            reject(err)
          } else resolve((axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`))
        })
      }
    })
  })
}

/**
 * Get MFA secret code
 * @param email
 * @returns secretCode and uri for QR-code
 */
export const getMfaSecretCode = async (email: string) => {
  /**
   * The cognito user
   */
  const cognitoUser = userPool.getCurrentUser()

  /**
   * Check user session
   * This confirms user is authenticated
   */
  await new Promise((res) => cognitoUser?.getSession(res))

  let secret

  await new Promise((resolve, reject) =>
    cognitoUser?.associateSoftwareToken({
      associateSecretCode: (secretCode) => {
        resolve((secret = secretCode))
      },
      onFailure: (err) => {
        reject(err)
      }
    })
  )

  return secret
    ? {
        key: secret,
        uri: `otpauth://totp/Spine:${email}?secret=${secret}&issuer=Spine&algorithm=SHA1&digits=6&period=30`
      }
    : undefined
}

/**
 * Clears cognito sessions from local storage.
 */
export const clearCognitoSessions = () => {
  for (const key in localStorage) {
    if (key.includes('CognitoIdentityServiceProvider')) {
      localStorage.removeItem(key)
    }
  }
}
